/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { ProcessStepsDaoService } from '../../dao/process-steps-dao.service';
import { ProcessStepsService } from '../process-steps.service';
import { ProcessStep, ProcessStepDTO } from 'src/app/_model/process-step';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads App Parameters
 */
@Injectable()
export class ProcessStepsLogicService implements ProcessStepsService {
  public processSteps$ = new BehaviorSubject<ProcessStep[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> = new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private processStepsDaoService: ProcessStepsDaoService,
    private authenticationService: AuthenticationService
  ) {
    this.initialize();
  }

  // Public Functions
  public getProcessStepById(id: IMSId): ProcessStep {
    if (id === undefined) {
      return undefined
    }

    return this.processSteps$.getValue().find((ps) => {
      if (ps.id.imsId == id.imsId && ps.id.secondaryId == id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  // Resetable
  public async reset() {
    await this.processStepsDaoService.reset();
  }

  public async clear() {
    await this.processStepsDaoService.clear();
    this.processSteps$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteProcessSteps(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING)
    try {
      await this.loadLocalProcessSteps();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED)
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE)
    }
  }

  // Private Functions
  private async loadLocalProcessSteps() {
    try {
      this.logger.log(this.constructor.name, '- Load local ProcessSteps');
      let processSteps: ProcessStep[] = await this.processStepsDaoService.getProcessSteps();

      this.processSteps$.next(processSteps);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local ProcessSteps: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemoteProcessSteps(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load remote ProcessSteps from IMS'
      );

      try {
        let processSteps: ProcessStep[] = [];
        let processStepsDto: ProcessStepDTO[] = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'syncprocesssteps',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              synckey: syncKey
            }
          })
        );

        this.logger.log(
          this.constructor.name,
          'Downloaded ProcessStepsDTO: ',
          processStepsDto
        );

        for (const processStepDto of processStepsDto) {
          processSteps.push(ProcessStep.fromDto(processStepDto));
        }

        this.logger.log(
          this.constructor.name,
          'Downloaded ProcessSteps: ',
          processSteps
        );

        await this.processStepsDaoService.saveProcessSteps(processSteps);
        await this.processSteps$.next(processSteps);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading ProcessSteps: ',
          error?.message
        );
        throw error;
      }
    } else {
      let error = new Error(
        'Cannot download ProcessSteps - user not authenticated'
      );
      this.logger.error(this.constructor.name, error?.message);
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {

  }

}
